/**
 * Support (tickets) service for the ms-agents backend (Flex Comms module).
 *
 * Each function takes the ms-agents verb (imported from `commsApi.js`) as its
 * first argument. Return-shape note: `get`/`patch`/`put` return `response.data`;
 * `post` returns `{ data, status }`.
 *
 * Everything from `/api/v1/support/*` lives here, including the v1.9 support
 * workspace surface (saved views, view counts, ticket editing, snooze, merge,
 * bulk actions, activity trail, macros, tags, presence, metrics and routing).
 *
 * Company scoping and employee gating are enforced server-side: a client token
 * gets 403, and a ticket belonging to another company answers 404 (not 403 —
 * that would confirm it exists).
 */
import { buildQuery } from "./_query";
import { wrapCommsError } from "./_error";

/**
 * Wrap an ms-agents call so every failure surfaces as an `Error` carrying the
 * backend's own message *and its HTTP status*. Keeps each endpoint below to its
 * request line.
 *
 * The status matters here: a 403 on a support room means "you are not a
 * participant", which is a permanent answer for that room until the agent picks
 * the ticket up — callers use it to stop retrying rather than hammering an
 * endpoint that cannot start succeeding.
 *
 * @param {string} fallbackMessage - Used when the response carries no message.
 * @param {() => Promise<*>} run - The request thunk.
 * @returns {Promise<*>}
 */
const callSupport = async (fallbackMessage, run) => {
  try {
    return await run();
  } catch (err) {
    throw wrapCommsError(err, fallbackMessage);
  }
};

/* -------------------------------------------------------------------------- */
/* Queue                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * List support tickets.
 *
 * Scoping (server-side): staff get the full company queue by default;
 * `mine: true` opts a staff caller into their own tickets. Non-staff callers
 * always get only their own tickets (`assignee`/`mine` are ignored), except
 * `unassigned: true` — the pick-up queue — which is visible to everyone.
 *
 * `view` is the v1.9 saved-views rail and replaces filtering in the browser.
 * Note that snoozed tickets are excluded unless `view: "snoozed"` or an
 * explicit `status` is passed, and `breached` is a server-side predicate —
 * filtering it client-side silently returns the wrong answer past page one.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ view, status, assignee, unassigned, mine,
 *   department, type, priority, tag, waiting_on, breached, category, sort,
 *   limit, offset }`.
 * @returns {Promise<{ tickets: Array<Object>, limit: number, offset: number }>}
 */
export const listTickets = async (get, options = {}) =>
  callSupport("Error fetching tickets", () =>
    get(`/api/v1/support/tickets${buildQuery(options)}`)
  );

/**
 * Sidebar counts for every saved view plus the per-department breakdown — one
 * call for all twelve chips. The counts use the same predicates as the views
 * they label, so paging the queue to count it is never necessary.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ mine }`.
 * @returns {Promise<{ views: Object<string, number>, departments: Object<string, number> }>}
 */
export const getViewCounts = async (get, options = {}) =>
  callSupport("Error fetching view counts", () =>
    get(`/api/v1/support/views/counts${buildQuery(options)}`)
  );

/**
 * Get a single support ticket by room id (adds participants, csat, escalations).
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} roomId - Room id backing the ticket.
 * @returns {Promise<Object>} Ticket detail.
 */
export const getTicket = async (get, roomId) =>
  callSupport("Error fetching ticket", () => get(`/api/v1/support/tickets/${roomId}`));

/**
 * Create a support ticket. `client_id` (create-on-behalf) is staff-only.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} payload - `{ company_id, subject, category?, client_id?,
 *   department?, type?, priority? }`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const createTicket = async (post, payload) =>
  callSupport("Error creating ticket", () => post(`/api/v1/support/tickets`, payload));

/* -------------------------------------------------------------------------- */
/* Editing + ownership                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Edit a ticket. Only the fields sent are written.
 *
 * `tags` **replaces** the whole set — send the full list. Omitting `tags`
 * leaves them alone; `[]` clears them. Unknown tag names are created for the
 * company automatically. Changing `priority`/`department` re-targets the SLA
 * policy for clocks still running; a first response that already happened is
 * never rewound.
 *
 * @param {Function} patch - ms-agents PATCH verb.
 * @param {string} roomId - Room id backing the ticket.
 * @param {Object} payload - `{ priority?, department?, type?, subject?, tags? }`.
 * @returns {Promise<Object>} Updated ticket.
 */
export const patchTicket = async (patch, roomId, payload) =>
  callSupport("Error updating ticket", () =>
    patch(`/api/v1/support/tickets/${roomId}`, payload)
  );

/**
 * Assign a ticket to an agent (omit `agentUserId` to self-assign).
 *
 * Any employee may pick up an unassigned ticket; assigning to someone else
 * requires staff tier. The backend normalizes whatever id it is given to the
 * assignee's canonical `user_id`, so an `Employees.id` from a staff picker
 * does not need resolving client-side.
 *
 * @param {Function} patch - ms-agents PATCH verb.
 * @param {string} roomId - Room id backing the ticket.
 * @param {string} [agentUserId] - Agent user id, or undefined for self-assign.
 * @returns {Promise<Object>} Updated ticket.
 */
export const assignTicket = async (patch, roomId, agentUserId) =>
  callSupport("Error assigning ticket", () =>
    patch(
      `/api/v1/support/tickets/${roomId}/assign`,
      agentUserId ? { agent_user_id: agentUserId } : {}
    )
  );

/**
 * Release a ticket back to the shared pool. Releasing your own is always
 * allowed; releasing someone else's needs staff tier.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id backing the ticket.
 * @param {string} [reason] - Optional release reason for the activity trail.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const unassignTicket = async (post, roomId, reason) =>
  callSupport("Error unassigning ticket", () =>
    post(`/api/v1/support/tickets/${roomId}/unassign`, reason ? { reason } : {})
  );

/**
 * Escalate a ticket to another agent.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id backing the ticket.
 * @param {Object} payload - `{ agent_user_id?, reason? }`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const escalateTicket = async (post, roomId, payload) =>
  callSupport("Error escalating ticket", () =>
    post(`/api/v1/support/tickets/${roomId}/escalate`, payload)
  );

/**
 * Resolve a ticket (posts the CSAT card to the client).
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id backing the ticket.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const resolveTicket = async (post, roomId) =>
  callSupport("Error resolving ticket", () =>
    post(`/api/v1/support/tickets/${roomId}/resolve`)
  );

/**
 * Reopen a resolved ticket (within the 24h reopen window); restarts the
 * next-response clock.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id backing the ticket.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const reopenTicket = async (post, roomId) =>
  callSupport("Error reopening ticket", () =>
    post(`/api/v1/support/tickets/${roomId}/reopen`)
  );

/**
 * Snooze a ticket out of the open queue until a moment in time. Pass
 * `until: null` to un-snooze.
 *
 * A snoozed ticket is still writable — it leaves the queue and the SLA sweep
 * skips it, but the room is not frozen, so a customer reply is never dropped.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id backing the ticket.
 * @param {Object} payload - `{ until: string|null, reason? }`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const snoozeTicket = async (post, roomId, payload) =>
  callSupport("Error snoozing ticket", () =>
    post(`/api/v1/support/tickets/${roomId}/snooze`, payload)
  );

/**
 * Merge this ticket into another. Merge is **link-only** — messages stay in
 * their original room (moving them would break the per-room `seq` every
 * pagination cursor and read receipt depends on). The loser points at the
 * winner, stops its clocks, closes, and its room is archived.
 *
 * 400 merging into itself · 403 across companies · 409 already merged.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id of the ticket being merged away.
 * @param {Object} payload - `{ target_room_id, notify_customer? }`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const mergeTicket = async (post, roomId, payload) =>
  callSupport("Error merging ticket", () =>
    post(`/api/v1/support/tickets/${roomId}/merge`, payload)
  );

/**
 * Apply one action to up to 100 tickets.
 *
 * **Partial success is normal** — one room's failure never aborts the rest, so
 * always read `results` per row rather than treating a 200 as "all done". Ids
 * outside the caller's companies come back as `"not found"`.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} payload - `{ room_ids: string[], action: 'assign'|'unassign'|
 *   'priority'|'snooze'|'resolve'|'tag' }` plus the matching field
 *   (`agent_user_id` / `priority` / `until` / `tags`).
 * @returns {Promise<{ data: { results: Array<{ room_id: string, ok: boolean, error: string|null }> }, status: number }>}
 */
export const bulkTicketAction = async (post, payload) =>
  callSupport("Error applying bulk action", () =>
    post(`/api/v1/support/tickets/bulk`, payload)
  );

/* -------------------------------------------------------------------------- */
/* Trail, context and collision detection                                      */
/* -------------------------------------------------------------------------- */

/**
 * Ticket audit trail, newest first. `actor_user_id: null` means the system did
 * it — the SLA sweep, the round-robin router, or a snooze waking up.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} roomId - Room id backing the ticket.
 * @param {Object} [options] - `{ limit, offset }`.
 * @returns {Promise<{ events: Array<Object> }>}
 */
export const getTicketActivity = async (get, roomId, options = {}) =>
  callSupport("Error fetching ticket activity", () =>
    get(`/api/v1/support/tickets/${roomId}/activity${buildQuery(options)}`)
  );

/**
 * A requester's ticket history for the context rail, with CSAT per ticket.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} clientId - Client user id.
 * @param {Object} [options] - `{ limit, offset }`.
 * @returns {Promise<{ tickets: Array<Object> }>}
 */
export const getClientTickets = async (get, clientId, options = {}) =>
  callSupport("Error fetching client tickets", () =>
    get(`/api/v1/support/clients/${clientId}/tickets${buildQuery(options)}`)
  );

/**
 * Heartbeat "I am looking at this ticket" for collision detection. Mirrors the
 * room `/typing` call: transient, nothing persisted, expires on its own after
 * ~30s so a closed laptop stops claiming the ticket. Fans out `TICKET_VIEWING`
 * on the ticket's own `support:{ticket_id}` channel and never reaches the bell.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id backing the ticket.
 * @param {boolean} viewing - True on open/heartbeat, false on blur.
 * @returns {Promise<{ data: *, status: number }>}
 */
export const setTicketViewing = async (post, roomId, viewing) =>
  callSupport("Error updating ticket viewing state", () =>
    post(`/api/v1/support/tickets/${roomId}/viewing`, { viewing })
  );

/* -------------------------------------------------------------------------- */
/* Team, presence and metrics                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Current per-agent ticket workload (open counts, presence, capacity,
 * departments, at_capacity).
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ mine }`.
 * @returns {Promise<{ agents: Array<Object> }>}
 */
export const getAgentsWorkload = async (get, options = {}) =>
  callSupport("Error fetching agents workload", () =>
    get(`/api/v1/support/agents/workload${buildQuery(options)}`)
  );

/**
 * The caller's own declared availability. An agent with no presence row yet
 * reads back as `offline` rather than 404, so the toggle renders on first load.
 *
 * The response carries `company_id` (v1.9.1) — the tenant boundary the
 * round-robin pool, the `TICKET_CREATED` fan-out and the team strip all filter
 * on. A `null` there means the agent is in **no** routing pool at all: it fails
 * closed rather than matching every tenant, so they silently stop being
 * auto-assigned. Any `PUT` re-resolves it.
 *
 * @param {Function} get - ms-agents GET verb.
 * @returns {Promise<Object>} `{ agent_user_id, status, capacity, departments, company_id }`.
 */
export const getAgentPresence = async (get) =>
  callSupport("Error fetching agent presence", () =>
    get(`/api/v1/support/agents/me/presence`)
  );

/**
 * Set the caller's own declared availability. Presence is *declared*, not
 * socket connectivity — an agent can be connected and still Away, and
 * auto-assign respects the toggle. `capacity` and `departments` are left
 * untouched when omitted, so the top-bar toggle can send just `status`.
 *
 * `company_id` is stripped before sending: the backend resolves it from the
 * employee record and never accepts it from the body, because a
 * client-supplied value would be a way into another company's routing ring.
 * Echoing back a row we just read would be exactly that request.
 *
 * @param {Function} put - ms-agents PUT verb.
 * @param {Object} payload - `{ status: 'available'|'away'|'offline', capacity?, departments? }`.
 * @returns {Promise<Object>} Updated presence row.
 */
export const setAgentPresence = async (put, payload) => {
  const { company_id: _companyId, ...body } = payload || {};
  return callSupport("Error updating agent presence", () =>
    put(`/api/v1/support/agents/me/presence`, body)
  );
};

/**
 * Support desk metrics for the top strip (staff only).
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ days }` (defaults to 30 server-side).
 * @returns {Promise<Object>} `{ median_first_response_seconds, avg_resolution_seconds,
 *   csat_avg, csat_count, open_tickets, breached_tickets, window_days }`.
 */
export const getSupportMetrics = async (get, options = {}) =>
  callSupport("Error fetching support metrics", () =>
    get(`/api/v1/support/metrics${buildQuery(options)}`)
  );

/* -------------------------------------------------------------------------- */
/* Macros and tags                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Canned responses. A macro with no `department` shows in every pool, and the
 * `department` filter includes those.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ department }`.
 * @returns {Promise<{ macros: Array<Object> }>}
 */
export const listMacros = async (get, options = {}) =>
  callSupport("Error fetching macros", () =>
    get(`/api/v1/support/macros${buildQuery(options)}`)
  );

/**
 * The company's tag vocabulary for the filter and the header chips.
 *
 * @param {Function} get - ms-agents GET verb.
 * @returns {Promise<{ tags: Array<Object> }>}
 */
export const listTags = async (get) =>
  callSupport("Error fetching tags", () => get(`/api/v1/support/tags`));

/* -------------------------------------------------------------------------- */
/* SLA policies and routing (manager tier)                                     */
/* -------------------------------------------------------------------------- */

/**
 * Round-robin routing configuration, per `(company, department)` with a NULL
 * department acting as the company catch-all.
 *
 * @param {Function} get - ms-agents GET verb.
 * @returns {Promise<{ rules: Array<Object> }>}
 */
export const getRoutingRules = async (get) =>
  callSupport("Error fetching routing rules", () =>
    get(`/api/v1/support/routing-rules`)
  );

/**
 * Save a routing rule. When enabled, a new ticket goes to the next `available`
 * agent in the pool who is under `capacity`; if nobody is eligible it stays in
 * the shared pool.
 *
 * @param {Function} put - ms-agents PUT verb.
 * @param {Object} payload - `{ company_id, department?, is_enabled, strategy }`.
 * @returns {Promise<Object>} Updated rule.
 */
export const putRoutingRules = async (put, payload) =>
  callSupport("Error saving routing rules", () =>
    put(`/api/v1/support/routing-rules`, payload)
  );
